import React, { useEffect, useMemo, useState } from 'react';
import API_BASE_URL from '../config';
import HomeButton from '../components/HomeButton';
import '../styles/Calendario.css';

// Vista calendario solo giornaliera con una fetch per giorno selezionato
const Calendario = () => {
    const [currentDate, setCurrentDate] = useState(new Date());
    const [startDate, setStartDate] = useState(new Date().toISOString().split('T')[0]);
    const [events, setEvents] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [lastFetch, setLastFetch] = useState(null);

    const formatISO = (date) => new Date(date).toISOString().split('T')[0];
    const getMinDate = () => new Date().toISOString().split('T')[0];
    const getMaxDate = () => {
        const d = new Date();
        d.setDate(d.getDate() + 7);
        return d.toISOString().split('T')[0];
    };

    const fetchCalendar = async (opts = {}) => {
        const dateParam = opts.date || startDate;
        const daysParam = 1;
        if (!dateParam) return;

        setLoading(true);
        setError('');
        try {
            const res = await fetch(`${API_BASE_URL}/api/bookings/calendar?date=${dateParam}&days=${daysParam}`);
            const data = await res.json();
            if (!res.ok || !data.success) {
                throw new Error(data.message || data.error || 'Errore caricamento calendario');
            }
            setEvents(data.events || []);
            setLastFetch(new Date());
        } catch (err) {
            console.error('Errore calendario:', err);
            setError(err.message);
            setEvents([]);
        } finally {
            setLoading(false);
        }
    };

    // Aggiorna il giorno selezionato
    useEffect(() => {
        setStartDate(formatISO(currentDate));
    }, [currentDate]);

    // primo load + ogni cambio intervallo
    useEffect(() => {
        fetchCalendar();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [startDate]);

    // polling leggero
    useEffect(() => {
        const id = setInterval(() => {
            fetchCalendar();
        }, 30000);
        return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [startDate]);

    const eventsByDay = useMemo(() => {
        const map = new Map();
        events.forEach(ev => {
            if (!ev.start) return;
            const dayKey = formatISO(ev.start);
            if (!map.has(dayKey)) map.set(dayKey, []);
            map.get(dayKey).push(ev);
        });
        map.forEach(list => list.sort((a, b) => new Date(a.start) - new Date(b.start)));
        return map;
    }, [events]);

    const formatTime = (iso) => {
        if (!iso) return 'N/D';
        const d = new Date(iso);
        return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    };

    const changeDate = (direction) => {
        const d = new Date(currentDate);
        d.setDate(d.getDate() + direction);
        setCurrentDate(d);
    };

    const renderDay = () => {
        const dayKey = formatISO(currentDate);
        const list = eventsByDay.get(dayKey) || [];
        return (
            <div className="day-view-container">
                {list.length === 0 && <p style={{ padding: '10px' }}>Nessun evento oggi.</p>}
                {list.length > 0 && (
                    <table className="day-view-table">
                        <thead>
                            <tr>
                                <th>Orario</th>
                                <th>Dettagli</th>
                            </tr>
                        </thead>
                        <tbody>
                            {list.map(ev => (
                                <tr key={ev.id}>
                                    <td className="hour-cell">{formatTime(ev.start)}</td>
                                    <td className="day-events-cell">
                                        <div className="calendar-event" style={{ borderLeftColor: 'var(--primary)' }}>
                                            <span className="event-title">{ev.summary || 'Prenotazione'}</span>
                                            {ev.guests ? <span style={{ marginLeft: 8, color: 'var(--text-muted)' }}>👥 {ev.guests}</span> : null}
                                            {ev.attendees && ev.attendees.length > 0 ? (
                                                <span style={{ marginLeft: 8, color: 'var(--text-muted)' }}>📧 {ev.attendees[0]}</span>
                                            ) : null}
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </div>
        );
    };

    return (
        <div className="calendar-page">
            <HomeButton />
            <div className="calendar-wrapper">
            <div className="calendar-header">
                <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                    <div className="calendar-nav">
                        <button className="calendar-nav-button" onClick={() => changeDate(-1)}>
                            <i className="bi bi-chevron-left"></i>
                        </button>
                        <span className="calendar-month-title">
                            {currentDate.toLocaleDateString('it-IT', { month: 'long', year: 'numeric' })}
                        </span>
                        <button className="calendar-nav-button" onClick={() => changeDate(1)}>
                            <i className="bi bi-chevron-right"></i>
                        </button>
                    </div>
                    <input
                        type="date"
                        value={formatISO(currentDate)}
                        onChange={(e) => {
                            const iso = e.target.value;
                            if (!iso) return;
                            // clamp within [today, today+7]
                            const min = getMinDate();
                            const max = getMaxDate();
                            const clamped = iso < min ? min : iso > max ? max : iso;
                            setCurrentDate(new Date(clamped));
                        }}
                        className="calendar-input"
                        min={getMinDate()}
                        max={getMaxDate()}
                    />
                    <button
                        className="add-btn"
                        type="button"
                        onClick={() => fetchCalendar()}
                        disabled={loading}
                        title="Aggiorna calendario"
                    >
                        {loading ? '...' : 'Aggiorna'}
                    </button>
                </div>
            </div>

            {error && <div className="message error">❌ {error}</div>}
            {lastFetch && (
                <div className="message-meta" style={{ marginBottom: '8px' }}>
                    Ultimo aggiornamento: {lastFetch.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' })}
                </div>
            )}

            <div className="calendar-content">
                {loading && <p style={{ padding: '10px' }}>Carico eventi...</p>}
                {!loading && events.length === 0 && <p style={{ padding: '10px' }}>Nessun evento in questo giorno.</p>}
                {!loading && events.length > 0 && renderDay()}
            </div>
            </div>
        </div>
    );
};

export default Calendario;
